import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useParams } from 'react-router-dom';
import DataOps from '../../data/DataOps';

const CoachPublicProfile: React.FC = () => {
    const { coachId } = useParams();
    const [coach, setCoach] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCoach = async () => {
            if (!coachId) return;
            setLoading(true);
            try {
                let coachData = await DataOps.fetchDataFromFirebase('coach', coachId);
                if (!coachData) {
                    coachData = await DataOps.getHostAsCoach(coachId);
                }
                setCoach(coachData || null);
            } catch (err) {
                console.error('Failed to load coach profile', err);
                setCoach(null);
            } finally {
                setLoading(false);
            }
        };

        fetchCoach();
    }, [coachId]);

    if (loading) {
        return (
            <View style={styles.container}> 
                <Text style={styles.subtleText}>Loading coach profile…</Text>
            </View>
        );
    }

    if (!coach) {
        return (
            <View style={styles.container}> 
                <Text style={styles.subtleText}>Coach not found.</Text>
            </View>
        );
    }

    const displayName = `${coach.firstName || ''} ${coach.lastName || ''}`.trim() || 'Coach';
    const bio: string | undefined = coach.bio;
    const rating: number | undefined = coach.rating;
    const numReviews: number | undefined = coach.numReviews;
    const years: number | undefined = coach.experienceYears ?? coach.yearsOfExperience;
    const languagesList: string[] = Array.isArray(coach.languages)
        ? coach.languages
        : (typeof coach.languages === 'string' && coach.languages
            ? coach.languages.split(',').map((s: string) => s.trim()).filter(Boolean)
            : []);
    const certifications: any[] = Array.isArray(coach.certifications) ? coach.certifications : [];
    const backgroundCheck = coach.backgroundCheck as { verified?: boolean; lastChecked?: string } | undefined;

    const formatDate = (iso?: string | null) => {
        if (!iso) return undefined;
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return undefined;
            return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        } catch {
            return undefined;
        }
    };

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Image
                    source={{ uri: coach.profileImage || 'https://upload.wikimedia.org/wikipedia/commons/9/99/Sample_User_Icon.png' }}
                    style={styles.avatar}
                />
                <View style={{ flex: 1 }}>
                    <Text style={styles.name}>{displayName}</Text>
                    {coach.accountType === 'host' && (
                        <Text style={styles.badge}>Host</Text>
                    )}
                </View>
            </View>

            {/* Summary Stats */}
            {(years || rating || numReviews) && (
                <View style={styles.statsCard}>
                    {typeof years === 'number' && (
                        <View style={styles.statItem}>
                            <MaterialCommunityIcons name="badge-account-outline" size={18} color="#374151" />
                            <Text style={styles.statValue}>{years}</Text>
                            <Text style={styles.statLabel}>yrs experience</Text>
                        </View>
                    )}
                    {(typeof rating === 'number' || typeof numReviews === 'number') && (
                        <View style={styles.statItem}>
                            <MaterialCommunityIcons name="star" size={18} color="#F59E0B" />
                            <Text style={styles.statValue}>{typeof rating === 'number' ? rating.toFixed(1) : '—'}</Text>
                            <Text style={styles.statLabel}>{typeof numReviews === 'number' ? `${numReviews} reviews` : 'reviews'}</Text>
                        </View>
                    )}
                </View>
            )}

            {/* Bio */}
            {bio && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>About</Text>
                    <Text style={styles.paragraph}>{bio}</Text>
                </View>
            )}

            {/* Certifications */}
            {certifications.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Certifications</Text>
                    <View style={{ marginTop: 8 }}>
                        {certifications.map((c: any, idx: number) => {
                            const isObject = c && typeof c === 'object';
                            const name = isObject ? c.name : String(c);
                            const issuer = isObject ? c.issuer : undefined;
                            const verified = isObject ? c.verified : undefined;
                            const expiresAt = isObject ? c.expiresAt : undefined;
                            const expiresLabel = formatDate(expiresAt);
                            return (
                                <View key={idx} style={styles.certRow}>
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.certName}>{name}</Text>
                                        <View style={styles.certMetaRow}>
                                            {issuer && <Text style={styles.certMeta}>Issuer: {issuer}</Text>}
                                            {expiresLabel && <Text style={styles.certMeta}>Expires: {expiresLabel}</Text>}
                                        </View>
                                    </View>
                                    {verified && (
                                        <View style={styles.verifiedPill}>
                                            <MaterialCommunityIcons name="check-decagram" size={14} color="#10B981" />
                                            <Text style={styles.verifiedText}>Verified</Text>
                                        </View>
                                    )}
                                </View>
                            );
                        })}
                    </View>
                </View>
            )}

            {/* Background Check */}
            {backgroundCheck && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Background Check</Text>
                    <View style={styles.bgRow}>
                        <View style={styles.bgStatus}>
                            <MaterialCommunityIcons name={backgroundCheck.verified ? 'shield-check' : 'shield-alert'} size={18} color={backgroundCheck.verified ? '#10B981' : '#EF4444'} />
                            <Text style={[styles.bgStatusText, { color: backgroundCheck.verified ? '#065F46' : '#991B1B' }]}>
                                {backgroundCheck.verified ? 'Verified' : 'Unverified'}
                            </Text>
                        </View>
                        {backgroundCheck.lastChecked && (
                            <Text style={styles.bgMeta}>Last checked: {formatDate(backgroundCheck.lastChecked)}</Text>
                        )}
                    </View>
                </View>
            )}

            {/* Languages */}
            {languagesList.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Languages</Text>
                    <View style={styles.langChips}>
                        {languagesList.map((lang, i) => (
                            <View key={`${lang}-${i}`} style={styles.langChip}>
                                <Text style={styles.langChipText}>{lang}</Text>
                            </View>
                        ))}
                    </View>
                </View>
            )}

            {/* Contact */}
            {(coach.email || coach.phone) && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Contact</Text>
                    {!!coach.email && (
                        <View style={styles.row}> 
                            <Text style={styles.label}>Email</Text>
                            <Text style={styles.value}>{coach.email}</Text>
                        </View>
                    )}
                    {!!coach.phone && (
                        <View style={styles.row}> 
                            <Text style={styles.label}>Phone</Text>
                            <Text style={styles.value}>{coach.phone}</Text>
                        </View>
                    )}
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        maxWidth: 900,
        marginHorizontal: 'auto' as any,
        paddingHorizontal: 20,
        paddingVertical: 24,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 24,
    },
    avatar: {
        width: 96,
        height: 96,
        borderRadius: 48,
        marginRight: 16,
    },
    name: {
        fontSize: 24,
        fontWeight: '700',
        color: '#222',
    },
    badge: {
        marginTop: 6,
        fontSize: 12,
        fontWeight: '600',
        color: '#2196F3',
    },
    statsCard: {
        flexDirection: 'row',
        gap: 16,
        padding: 12,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 12,
        marginBottom: 16,
    },
    statItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    statValue: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
    },
    statLabel: {
        fontSize: 12,
        color: '#6B7280',
        marginLeft: 4,
    },
    section: {
        backgroundColor: '#fff',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E0E0E0',
        padding: 16,
        marginBottom: 16,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 8,
    },
    paragraph: {
        fontSize: 14,
        color: '#374151',
        lineHeight: 20,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#F0F0F0',
    },
    label: {
        fontSize: 14,
        fontWeight: '600',
        color: '#666',
    },
    value: {
        fontSize: 14,
        color: '#222',
    },
    certRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    certName: {
        fontSize: 14,
        fontWeight: '600',
        color: '#1F2937',
    },
    certMetaRow: {
        flexDirection: 'row',
        gap: 12,
        marginTop: 2,
    },
    certMeta: {
        fontSize: 12,
        color: '#6B7280',
    },
    verifiedPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: '#ECFDF5',
        borderWidth: 1,
        borderColor: '#A7F3D0',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
    },
    verifiedText: {
        fontSize: 12,
        color: '#065F46',
        fontWeight: '600',
    },
    bgRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    bgStatus: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    bgStatusText: {
        fontSize: 14,
        fontWeight: '700',
    },
    bgMeta: {
        fontSize: 12,
        color: '#6B7280',
    },
    langChips: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    langChip: {
        backgroundColor: '#F3F4F6',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 999,
    },
    langChipText: {
        fontSize: 12,
        color: '#1F2937',
        fontWeight: '600',
    },
    subtleText: {
        textAlign: 'center',
        color: '#888',
    },
});

export default CoachPublicProfile;


